import {groupBy, includes} from "lodash-es";
import {RequestState} from "modules/enums";
import type {getMatchByIdSelector} from "modules/selectors/units";
import type {TStore} from "modules/types/store";
import {isUnitLocked} from "modules/utils/match";
import {createSelector} from "reselect";

export const getStreakStore = (store: TStore) => store.streak;

export const getPredictionsList = createSelector([getStreakStore], (streak) => streak.predictions);

export const getRecentHistoryList = createSelector(
	[getStreakStore],
	(streak) => streak.resentHistory
);

export const getPredictionsByRoundId = createSelector(
	[getStreakStore],
	(streak) => (roundId?: number) => {
		return roundId ? streak.predictions.filter((it) => it.roundId === roundId) : [];
	}
);

export const getLastPredictionsByRoundId = createSelector(
	[getStreakStore],
	(streak) => (roundId?: number) => {
		return roundId ? streak.lastPredictions.filter((it) => it.roundId === roundId) : [];
	}
);


export const getPickCountForFutureRounds = createSelector(
	[getStreakStore],
	(streak) => (roundIds: number[]) => {
		return streak.predictions.filter((it) => includes(roundIds, it.roundId)).length;
	}
);

export const isPredictionsLocked = createSelector(
	[getPredictionsByRoundId],
	(picksSelector) => (roundId?: number) => {
		if (!roundId) {
			return false;
		}

		const picks = picksSelector(roundId);
		return picks.length === 2;
	}
);

export const isLoadedPredictions = createSelector(
	[getStreakStore],
	(streak) => streak.requestStatePicks === RequestState.RECEIVED
);

export const isLoadedFriendsPicks = createSelector(
	[getStreakStore],
	(streak) => streak.requestStateFriends === RequestState.RECEIVED
);

export const getPlayerPredictionsSelector = createSelector(
	[getPredictionsByRoundId],
	(predictionsSelector) => (id: number, roundId: number) => {
		return predictionsSelector(roundId).filter((it) => it.playerId === id);
	}
);

export const getHistoryPicks = createSelector([getStreakStore], (store) => store.history);

export const getFriendPicks = createSelector([getStreakStore], (store) => store.friendHistory);

export const getHistoryGroupedByRoundID = createSelector([getHistoryPicks], (history) =>
	groupBy(history.predictions, "roundId")
);

export const getFriendPicksByPlayerId = createSelector(
	[getStreakStore],
	(store) => (playerId: number) => store.friendPicksByPlayerId[playerId]
);

export const isOpenMulliganModal = createSelector(
	[getStreakStore],
	(store) => store.modals.mulliganModalState
);

export const getModalsState = createSelector([getStreakStore], (store) => store.modals);

export const isLockedByPredictionSelector = createSelector(
	[getPredictionsByRoundId],
	(predictionsSelector) =>
		(roundId: number, matchByIdSelector: ReturnType<typeof getMatchByIdSelector>) => {
			const predictions = predictionsSelector(roundId);
			return Boolean(
				predictions.find((it) => {
					const match = matchByIdSelector(it.unitId);
					return isUnitLocked(match);
				})
			);
		}
);
